import React from 'react';

/**
 * Hermetic-lock state pill for a single gate.
 *
 * Renders nothing for gates that aren't on the toggle_with_stop
 * wiring (no lock concept exists for them, and the empty surface
 * keeps the panel uncluttered). For wiring 2 gates, surfaces one
 * of THREE states — the third one (warning) is the bug fix that
 * shipped together with splitting hermetic-lock-enabled off
 * auto-lock-enabled:
 *
 *   🔒 נעול אוטומטית   — O2 energised, motor STOP broken, gate
 *                        physically cannot run. Always green.
 *   🔓 לא נעול         — operable. During active hours / when the
 *                        hermetic-lock toggle is off this is the
 *                        normal expected state — green.
 *   ⚠️ אמור להיות נעול — operable, but hermetic-lock is on and
 *                        we're outside active hours. Means the
 *                        enforcer didn't manage to engage O2 (device
 *                        offline, command stuck, hardware issue) —
 *                        a problem the customer should see at a
 *                        glance. Amber.
 *
 * The "should-be" half of the warning is computed server-side
 * (services.commands.should_be_hermetically_locked) and exposed
 * on the gate detail payload as ``should_be_hermetically_locked``.
 */
function HermeticLockIndicator({ wiringType, isLocked, shouldBeLocked }) {
  if (wiringType !== 'toggle_with_stop') {
    return null;
  }

  let palette;
  let title;
  if (isLocked) {
    palette = { fg: '#1e3a8a', bg: '#dbeafe', border: '#93c5fd', icon: '🔒', label: 'נעול אוטומטית' };
    title = 'מעגל הבטיחות פתוח — המנוע אינו יכול לפעול עד שחרור הנעילה';
  } else if (shouldBeLocked) {
    palette = { fg: '#92400e', bg: '#fffbeb', border: '#fde68a', icon: '⚠️', label: 'אמור להיות נעול' };
    title = 'מחוץ לשעות הפעילות והנעילה ההרמטית מופעלת — אך השער עדיין מאפשר תנועה. ייתכן שהפקודה נתקעה או שיש בעיית חומרה. כדאי לבדוק.';
  } else {
    palette = { fg: '#065f46', bg: '#d1fae5', border: '#6ee7b7', icon: '🔓', label: 'לא נעול' };
    title = 'מעגל הבטיחות סגור — השער ניתן לפעולה';
  }

  return (
    <div
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.3rem 0.65rem',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        borderRadius: '999px',
        fontSize: '0.78rem',
        fontWeight: 500,
        marginBottom: '0.85rem',
        fontFamily: 'Heebo, sans-serif',
      }}
    >
      <span aria-hidden="true">{palette.icon}</span>
      <span>{palette.label}</span>
    </div>
  );
}

export default HermeticLockIndicator;
